import { useEffect, useMemo, useState } from "react";
import { supabase, dbToDeal, loadPreProxySummaries, fetchAllRows } from "../../supabase";
import useMsaList from "../../hooks/useMsaList";
import {
  computeReviewPipelineFunnel,
  computeDealsAndListings,
  computeMaxStageReached,
  makeDateRange,
} from "../../services/reviewMetrics";
import { KILLED_STAGES } from "../../constants/dealStages";
import { collapseSlips } from "../../services/stageLog";
import ReviewPipelineFunnel from "./ReviewPipelineFunnel";
import ReviewPipelineTable from "./ReviewPipelineTable";
import ReviewKillReasons from "./ReviewKillReasons";
import { exportReviewXLSX, exportReviewJSON } from "../../services/reviewExport";

// ─── ReviewPage (Review Pipeline) ───────────────────────────────────────────
// The new default view. Loads every deal ever seen, renders the slide-style
// funnel across three lanes (Dead / Tracking / Active), and drives a
// filterable deals table below. Filter bar is F2-style: days-ago presets +
// quarter dropdown + custom date range picker, alongside MSA multi-select.
//
// Pre-Proxy 2025 numbers come from the `pre_proxy_summaries` table so the
// funnel is not blank when the filter window includes dates before Apr 2026.
//
// The Bias / Self-Review tab was retired for this iteration — we'll come back
// to it once the funnel model is settled.

const DAYS_AGO_PRESETS = [
  { n: 7,   label: "7d" },
  { n: 30,  label: "30d" },
  { n: 90,  label: "90d" },
  { n: 180, label: "6mo" },
  { n: 365, label: "1yr" },
];

// Quarter dropdown options — last 8 quarters back through 2024-Q1 (which
// covers all pre-Proxy data we've backfilled). Generated once at module load.
function buildQuarterOptions() {
  const now = new Date();
  const opts = [];
  let y = now.getUTCFullYear();
  let q = Math.floor(now.getUTCMonth() / 3) + 1;
  for (let i = 0; i < 12; i++) {
    opts.push({ year: y, q, label: `Q${q} ${y}` });
    q--;
    if (q === 0) { q = 4; y--; }
  }
  return opts;
}
const QUARTER_OPTIONS = buildQuarterOptions();

export default function ReviewPage({ onNavigateToDeal }) {
  const [msaFilter, setMsaFilter] = useState([]);
  const [filterKind, setFilterKind] = useState("all");        // 'all' | 'days' | 'quarter' | 'custom'
  const [daysN, setDaysN] = useState(30);
  const [quarterSel, setQuarterSel] = useState(QUARTER_OPTIONS[1]);   // last quarter default when user picks kind=quarter
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");

  const { msas: msaList } = useMsaList();

  // Resolve the current filterKind + inputs into a date range for
  // computeReviewPipelineFunnel/computeDealsAndListings.
  const dateRange = useMemo(() => {
    if (filterKind === "days")    return makeDateRange({ kind: "days_ago", n: daysN });
    if (filterKind === "quarter") return makeDateRange({ kind: "quarter", year: quarterSel.year, q: quarterSel.q });
    if (filterKind === "custom" && customStart && customEnd)
      return makeDateRange({ kind: "custom", start: customStart, end: customEnd });
    return makeDateRange("all");
  }, [filterKind, daysN, quarterSel, customStart, customEnd]);

  const [deals, setDeals] = useState([]);
  const [activityLog, setActivityLog] = useState([]);
  const [competition, setCompetition] = useState([]);
  const [preProxy, setPreProxy] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selection, setSelection] = useState({});             // funnel-cell filter
  const [exportMenuOpen, setExportMenuOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      const [dealsRes, actLog, compRes, pre] = await Promise.all([
        fetchAllRows(() => supabase.from("deals").select("*").eq("deal_flow", "pipeline")
          .order("created_at", { ascending: false }).order("id")),
        fetchAllRows(() => supabase.from("activity_log").select("entity_id, action, metadata, created_at")
          .eq("entity_type", "deal").eq("action", "stage_changed").order("created_at").order("id")),
        fetchAllRows(() => supabase.from("deal_competition").select("*").order("id")),
        loadPreProxySummaries(),
      ]);
      if (cancelled) return;
      setDeals((dealsRes.data || []).map(dbToDeal));
      setActivityLog(collapseSlips(actLog.data || []));
      setCompetition(compRes.data || []);
      setPreProxy(pre || []);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  const funnel = useMemo(() => computeReviewPipelineFunnel({
    deals, activityLog, dateRange, msas: msaFilter, preProxySummaries: preProxy,
  }), [deals, activityLog, dateRange, msaFilter, preProxy]);

  const dealsListings = useMemo(() => computeDealsAndListings({
    deals, listings: [], dateRange, msas: msaFilter,
  }), [deals, dateRange, msaFilter]);

  // Apply the funnel-cell selection to the deals table.
  const tableRows = useMemo(() => {
    if (!selection || (!selection.lane && !selection.stage)) return dealsListings;
    return dealsListings.filter(r => {
      const raw = r._raw;
      if (!raw) return false;

      // Stage-row selection (per lane sub-list)
      if (selection.stage) {
        return raw.stage === selection.stage;
      }

      // Lane selection
      const isKilled = KILLED_STAGES.includes(raw.stage);
      const laneMatch =
        selection.lane === "dead"     ? isKilled :
        selection.lane === "tracking" ? raw.stage === "Tracking" :
        selection.lane === "active"   ? (!isKilled && raw.stage !== "Tracking") :
        true;
      if (!laneMatch) return false;

      // Split selection (off/on market)
      if (selection.split) {
        const marketed = !!raw.marketed;
        if (selection.split === "on"  && !marketed) return false;
        if (selection.split === "off" &&  marketed) return false;
      }
      // Tier selection (LOI / B&F)
      if (selection.tier === "loi") {
        const maxStage = computeMaxStageReached(raw, activityLog);
        // reached LOI Submitted or beyond
        const stageRanks = { "In Diligence": 0, "LOI Submitted": 1, "Unsolicited Offer": 2, "Underwriting": 3, "Screening": 4, "Sourcing": 5, "Outreach": 5, "Tracking": 6, "Passed": 7, "Lost": 8, "Dead": 9 };
        if (stageRanks[maxStage] > stageRanks["LOI Submitted"]) return false;
      }
      if (selection.tier === "bf") {
        if (!raw.madeBestAndFinal) return false;
      }
      return true;
    });
  }, [dealsListings, selection, activityLog]);

  // Why-deals-die reads the SAME set the table is built from, before the
  // funnel-cell selection narrows it — so the reasons answer "in this window,
  // in these markets", not "in whichever cell happens to be clicked".
  const scopedDeals = useMemo(
    () => dealsListings.map((r) => r._raw).filter(Boolean),
    [dealsListings],
  );

  function toggleMsa(m) {
    setMsaFilter(prev => prev.includes(m) ? prev.filter(x => x !== m) : [...prev, m]);
  }

  // When the inline editor saves a deal, patch our local list so the funnel
  // and table re-derive off the new data without a full reload.
  function onDealEdited(updated) {
    setDeals(prev => prev.map(d => d.id === updated.id ? { ...d, ...updated } : d));
  }

  const preProxyLabels = (funnel?.preProxyMerged || []).map(r => r.period_label);

  return (
    <div className="max-w-7xl mx-auto px-6 py-8 space-y-5">
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white font-montserrat">Review Pipeline</h1>
          <p className="text-xs text-[#8a8a8a] mt-0.5">
            {dateRange.label}
            {msaFilter.length > 0 && <> · {msaFilter.join(", ")}</>}
            {loading && <span className="ml-2 text-[#f04f44]">loading…</span>}
          </p>
        </div>
        <div className="relative">
          <button
            onClick={() => setExportMenuOpen(v => !v)}
            className="text-sm px-4 py-2 rounded-lg border border-[#4a4a4a] text-[#b4b4b4] hover:border-pp-red/60 hover:text-[#f4f4f4] transition-colors">
            Export ▾
          </button>
          {exportMenuOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setExportMenuOpen(false)} />
              <div className="absolute right-0 top-full mt-1 z-50 bg-[#2c2c2c] border border-[#3a3a3a] rounded-lg shadow-xl py-1 w-56">
                <button
                  onClick={() => {
                    setExportMenuOpen(false);
                    exportReviewXLSX({ funnel, dealsListings: tableRows, competition, dateLabel: dateRange.label, msaFilter });
                  }}
                  className="w-full text-left px-3 py-2 text-sm text-[#f4f4f4] hover:bg-[#3a3a3a]">
                  <div className="font-semibold">Excel (.xlsx)</div>
                  <div className="text-[10px] text-[#8a8a8a]">Funnel · Deals · Competition · PP branded</div>
                </button>
                <button
                  onClick={() => {
                    setExportMenuOpen(false);
                    exportReviewJSON({ funnel, dealsListings: tableRows, competition, dateLabel: dateRange.label, msaFilter });
                  }}
                  className="w-full text-left px-3 py-2 text-sm text-[#f4f4f4] hover:bg-[#3a3a3a]">
                  <div className="font-semibold">JSON</div>
                  <div className="text-[10px] text-[#8a8a8a]">Raw datasets · analyst-friendly</div>
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Filter bar (F2: presets + quarter dropdown + custom range) */}
      <div className="bg-[rgba(44,44,44,0.6)] border border-[rgba(58,58,58,0.5)] rounded-xl p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-[#8a8a8a] uppercase tracking-wider font-semibold mr-1">Range</span>
          <button onClick={() => setFilterKind("all")}
            className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${filterKind === "all"
              ? "bg-pp-red border-pp-red text-white"
              : "bg-[#3a3a3a] border-[#4a4a4a] text-[#b4b4b4] hover:border-pp-red/60"}`}
            style={filterKind === "all" ? { color: "#fff" } : {}}>
            All time
          </button>
          {DAYS_AGO_PRESETS.map(p => (
            <button key={p.n} onClick={() => { setFilterKind("days"); setDaysN(p.n); }}
              className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${filterKind === "days" && daysN === p.n
                ? "bg-pp-red border-pp-red text-white"
                : "bg-[#3a3a3a] border-[#4a4a4a] text-[#b4b4b4] hover:border-pp-red/60"}`}
              style={filterKind === "days" && daysN === p.n ? { color: "#fff" } : {}}>
              {p.label}
            </button>
          ))}

          <div className="flex items-center gap-1 ml-2">
            <span className="text-[11px] text-[#8a8a8a] mr-1">Quarter</span>
            <select
              value={filterKind === "quarter" ? `${quarterSel.year}-${quarterSel.q}` : ""}
              onChange={(e) => {
                if (!e.target.value) return;
                const [y, q] = e.target.value.split("-").map(Number);
                setFilterKind("quarter"); setQuarterSel({ year: y, q });
              }}
              className="text-xs bg-[#3a3a3a] border border-[#4a4a4a] text-[#b4b4b4] rounded px-2 py-1.5">
              <option value="">— pick —</option>
              {QUARTER_OPTIONS.map(o => (
                <option key={`${o.year}-${o.q}`} value={`${o.year}-${o.q}`}>{o.label}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-1 ml-2">
            <span className="text-[11px] text-[#8a8a8a] mr-1">Custom</span>
            <input type="date" value={customStart}
              onChange={(e) => { setCustomStart(e.target.value); if (e.target.value && customEnd) setFilterKind("custom"); }}
              className="text-xs bg-[#3a3a3a] border border-[#4a4a4a] text-[#b4b4b4] rounded px-2 py-1" />
            <span className="text-[11px] text-[#8a8a8a]">→</span>
            <input type="date" value={customEnd}
              onChange={(e) => { setCustomEnd(e.target.value); if (customStart && e.target.value) setFilterKind("custom"); }}
              className="text-xs bg-[#3a3a3a] border border-[#4a4a4a] text-[#b4b4b4] rounded px-2 py-1" />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-[#8a8a8a] uppercase tracking-wider font-semibold mr-1">MSAs</span>
          <button onClick={() => setMsaFilter([])}
            className={`text-[11px] px-2.5 py-1 rounded-full border ${msaFilter.length === 0
              ? "bg-pp-red border-pp-red text-white"
              : "bg-[#3a3a3a] border-[#4a4a4a] text-[#b4b4b4] hover:border-pp-red/60"}`}
            style={msaFilter.length === 0 ? { color: "#fff" } : {}}>
            All
          </button>
          {msaList.map(m => (
            <button key={m} onClick={() => toggleMsa(m)}
              className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${msaFilter.includes(m)
                ? "bg-pp-red border-pp-red text-white"
                : "bg-[#3a3a3a] border-[#4a4a4a] text-[#b4b4b4] hover:border-pp-red/60"}`}
              style={msaFilter.includes(m) ? { color: "#fff" } : {}}>
              {m}
            </button>
          ))}
        </div>
      </div>

      {/* Funnel visual (2025-slide layout) */}
      <ReviewPipelineFunnel
        funnel={funnel}
        dateLabel={dateRange.label}
        onSelect={setSelection}
        selection={selection}
        preProxyLabels={preProxyLabels}
      />

      {/* Why deals die — scoped by the date range and MSA filter above */}
      <ReviewKillReasons deals={scopedDeals} dateLabel={dateRange.label} />

      {/* Deals grid (filtered by any funnel-cell selection) */}
      <ReviewPipelineTable
        rows={tableRows}
        deals={deals}
        competition={competition}
        onNavigateToDeal={onNavigateToDeal}
        onDealEdited={onDealEdited}
      />
    </div>
  );
}
